import {
  useLazyGetExternalOperatorShippingByNumberQuery,
  type ExternalOperatorShippingDetail,
} from '../../services/externalOperatorShippings.endpoints';
import { useAvailableAmendments } from '@/pages/Business/pages/Amendments/hooks/useAvailableAmendments';
import { useApplyAmendmentMutation } from '@/pages/Business/pages/Amendments/services';
import { useExternalOperatorAmendmentStorage } from './useExternalOperatorAmendmentStorage';
import { getErrorMessage } from '@/utils';
import { useState } from 'react';

export const useExternalOperatorApplyAmendmentController = () => {
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { draft, setShipping: persistShipping, updateDraft } = useExternalOperatorAmendmentStorage();

  const [shippingNumber, setShippingNumberState] = useState<string>(
    () => (draft.shippingNumber as string) ?? ''
  );
  const [shipping, setShippingState] = useState<ExternalOperatorShippingDetail | null>(
    () => (draft.shipping as ExternalOperatorShippingDetail) ?? null
  );

  const [searchKey, setSearchKey] = useState(0);

  const setShipping = (number: string, newShipping: ExternalOperatorShippingDetail | null) => {
    setShippingState(newShipping);
    setShippingNumberState(newShipping ? number : '');
    persistShipping(number, newShipping);
  };

  const [fetchShipping, { isFetching: isShippingLoading }] =
    useLazyGetExternalOperatorShippingByNumberQuery();

  const { amendmentOptions, isLoading: isAmendmentsLoading } = useAvailableAmendments(
    shipping ? Number(shippingNumber) : null
  );

  const [applyAmendment, { isLoading: isApplying }] = useApplyAmendmentMutation();

  const handleSearch = async (input: string) => {
    updateDraft({ searchInput: input });
    const trimmed = input.trim();
    try {
      const full = await fetchShipping({ number: trimmed }).unwrap();
      setShipping(trimmed, full);
    } catch (error) {
      setErrorMessage(getErrorMessage(error, 'No se encontró el pedido con ese número.'));
    }
  };

  const handleApply = async (args: { transitionId: string; reason: string; driverId?: string }) => {
    if (!shipping) return;
    try {
      await applyAmendment({ shippingNumber: Number(shippingNumber), ...args }).unwrap();
      setSuccessMessage('Enmienda aplicada correctamente.');
      setShipping('', null);
      setSearchKey((k) => k + 1);
    } catch (error) {
      setErrorMessage(
        getErrorMessage(error, 'Error al aplicar la enmienda. Verificá los datos e intentá de nuevo.')
      );
    }
  };

  return {
    shipping,
    isShippingLoading,
    searchKey,
    draftSearchInput: draft.searchInput as string | undefined,
    handleSearch,
    amendmentOptions,
    isAmendmentsLoading,
    handleApply,
    isApplying,
    successMessage,
    errorMessage,
    setSuccessMessage,
    setErrorMessage,
  };
};
